import { Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { map } from 'rxjs/operators';
import { EnvironmentService } from '../../../core/services/environment.service';
import { StorageMap } from '@ngx-pwa/local-storage';


enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
}

/**
 * This small component connects to a SignalR hub, listens to data on the specified hub name and broadcast event name,
 * and yields data upon received.
 * The communication is unidirectional - only listening for data, no data sending.
 * It assumes that a SignalR hub based URL is defined in an environment file.
 */
@Component({
  selector: 'upsc-signalrify',
  templateUrl: './signalrify.component.html',
  styleUrls: ['./signalrify.component.scss'],
})

export class SignalrifyComponent implements OnInit {
  @HostBinding('class') public hostClass = 'signalrify';

  @Input() public hubName: string;
  @Input() public broadcastEventName: string;
  @Output() public dataReceived = new EventEmitter<any>();

  public hubConnectionStatus: ConnectionStatus = ConnectionStatus.CONNECTING;
  private hubConnection: signalR.HubConnection;

  constructor(private environmentService: EnvironmentService,
              private storage: StorageMap,
  ) {
  }

  public async ngOnInit(): Promise<void> {
    await this.createSignalRConnection();
    this.createListener();
  }

  public async createSignalRConnection(): Promise<void> {
    const token = await this.storage.get('auth_token')
                          .pipe(map(result => result as string))
                          .toPromise();
    const signalRHubBaseUrl = this.environmentService.environment.signalRHubBaseUrl;
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(
        `${signalRHubBaseUrl}/${this.hubName}`,
        { accessTokenFactory: () => token },
      )
      .build();

    this.hubConnectionStatus = ConnectionStatus.CONNECTING;
    try {
      await this.hubConnection.start();
      this.hubConnectionStatus = ConnectionStatus.CONNECTED;
    } catch (e) {
      this.hubConnectionStatus = ConnectionStatus.DISCONNECTED;
      this.dataReceived.emit(null);
    }
  }

  private createListener(): void {
    this.hubConnection?.on(
      this.broadcastEventName,
      (message) => {
        this.dataReceived.emit(message);
      }
    )
  }
}
